import React from "react";
import { useNavigate } from "react-router-dom";
import {
    CheckCircleFilled,
    CloseCircleFilled,
    ExclamationCircleFilled,
    MinusCircleOutlined,
    PauseCircleOutlined,
    PlayCircleOutlined,
} from "@ant-design/icons";

import {
    Benchmark,
    Confidence,
    Deal,
    GuardrailPolicy,
    Lever,
    Outcome,
    Tone,
} from "../model/types";
import {
    AutonomyZone,
    ClauseStatus,
    clauseStatusesFor,
    getDealAutonomyZone,
    getPolicyForDeal,
    isLeverOutOfEnvelope,
} from "../model/api";
import { useTailSpend } from "../TailSpendContext";
import { StatusTag } from "./StatusTag";
import dealDrawerClasses from "./DealDrawer.module.less";
import classes from "./DealSidePanels.module.less";

interface SidePanelsProps {
    deal: Deal;
}

export const DealSidePanels: React.FC<SidePanelsProps> = ({ deal }) => {
    const { workspace, dispatch } = useTailSpend();
    const navigate = useNavigate();
    const policy = getPolicyForDeal(workspace, deal);
    const categoryName =
        workspace.categories.find((c) => c.id === deal.categoryId)?.name ?? "";
    const onTogglePause = () =>
        dispatch({
            type: "ToggleManualPause",
            dealId: deal.id,
            byName: workspace.currentUser.name,
        });
    return (
        <div className={classes.stack}>
            <div className={classes.policyHeader}>
                <span className={classes.policyHeaderLabel}>Policy:</span>{" "}
                <a
                    href="/superadmin/demo/tail-spend/guardrails"
                    className={classes.policyHeaderLink}
                    onClick={(e) => {
                        e.preventDefault();
                        navigate("/superadmin/demo/tail-spend/guardrails");
                    }}
                >
                    {categoryName}
                </a>
            </div>
            <AutonomyBandBox
                deal={deal}
                policy={policy}
                onTogglePause={onTogglePause}
            />
            {deal.envelope.length > 0 && (
                <LeversBox levers={deal.envelope} />
            )}
            <ComplianceBox deal={deal} policy={policy} />
            {deal.benchmark && <BenchmarkBox benchmark={deal.benchmark} />}
            {deal.outcome && <FinalTermsBox outcome={deal.outcome} />}
            {deal.outcome && <RecordsBox outcome={deal.outcome} />}
        </div>
    );
};

const Box: React.FC<{
    title: string;
    subtitle?: React.ReactNode;
    headerRight?: React.ReactNode;
    children: React.ReactNode;
    tourId?: string;
}> = ({ title, subtitle, headerRight, children, tourId }) => (
    <div className={classes.box} data-tour-id={tourId}>
        <div className={classes.boxTitle}>
            <span className={classes.boxTitleText}>
                <span>{title}</span>
                {subtitle && <span className={classes.boxSubtitle}>{subtitle}</span>}
            </span>
            {headerRight && (
                <span className={classes.boxHeaderRight}>{headerRight}</span>
            )}
        </div>
        <div className={classes.boxBody}>{children}</div>
    </div>
);

// ---------------------------------------------------------------------------
// 1. Autonomy band
// ---------------------------------------------------------------------------

const ZONE_LABEL: Record<AutonomyZone, string> = {
    autonomous: "Autonomous",
    esign: "Buyer e-sign",
    strategic: "Strategic team",
};

const ZONE_ROUTING: Record<AutonomyZone, string> = {
    autonomous: "Agent closes and writes back to ERP with no human review.",
    esign: "Agent negotiates; you countersign the order form before the PR is created.",
    strategic: "Agent stops at triage and hands the negotiation to the strategic team.",
};

const fmtUsdK = (n: number): string => {
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
    if (n >= 1_000) return `$${(n / 1_000).toFixed(n % 1_000 === 0 ? 0 : 1)}K`;
    return `$${n}`;
};

const AutonomyBandBox: React.FC<{
    deal: Deal;
    policy: GuardrailPolicy;
    onTogglePause: () => void;
}> = ({ deal, policy, onTogglePause }) => {
    const value = deal.annualValue.amount;
    const eSignMin = policy.buyerESignBand.min.amount;
    const eSignMax = policy.buyerESignBand.max.amount;
    const strategicAbove = policy.handToStrategicAbove.amount;
    const zone = getDealAutonomyZone(deal, policy);
    const paused = !!deal.manualPause;

    const axisMax = Math.max(value * 1.15, strategicAbove * 1.4);
    const pct = (n: number) => Math.min(100, Math.max(0, (n / axisMax) * 100));

    const eSignStartPct = pct(eSignMin);
    const eSignEndPct = pct(eSignMax);
    const strategicStartPct = pct(strategicAbove);
    const markerPct = pct(value);

    return (
        <Box
            title="Autonomy"
            tourId="dealAutonomy"
            headerRight={
                <button
                    type="button"
                    onClick={onTogglePause}
                    className={`${classes.autonomyToggle} ${
                        paused
                            ? classes.autonomyTogglePaused
                            : classes[`autonomyToggle_${zone}`]
                    }`}
                    title={paused ? "Resume agent" : "Pause agent"}
                >
                    {paused ? <PlayCircleOutlined /> : <PauseCircleOutlined />}
                    <span>{paused ? "Paused" : ZONE_LABEL[zone]}</span>
                </button>
            }
        >
            <div className={classes.bandRouting}>
                {paused
                    ? "You paused this deal. The agent will not act until you resume."
                    : ZONE_ROUTING[zone]}
            </div>

            <div className={classes.bandChart}>
                <div
                    className={classes.bandValueLabel}
                    style={{ left: `${markerPct}%` }}
                >
                    ${value.toLocaleString()}
                </div>
                <div className={classes.bandTrack}>
                    <div
                        className={`${classes.bandZoneFill} ${classes.zoneAuto}`}
                        style={{ left: 0, width: `${eSignStartPct}%` }}
                    />
                    <div
                        className={`${classes.bandZoneFill} ${classes.zoneEsign}`}
                        style={{
                            left: `${eSignStartPct}%`,
                            width: `${eSignEndPct - eSignStartPct}%`,
                        }}
                    />
                    <div
                        className={`${classes.bandZoneFill} ${classes.zoneStrategic}`}
                        style={{
                            left: `${strategicStartPct}%`,
                            width: `${100 - strategicStartPct}%`,
                        }}
                    />
                    <div
                        className={classes.dealMarker}
                        style={{ left: `${markerPct}%` }}
                        aria-label="Deal annual value"
                    />
                </div>
            </div>

            <div className={classes.bandTicks}>
                <span style={{ left: 0 }} className={classes.bandTick}>
                    $0
                </span>
                <span
                    style={{ left: `${eSignStartPct}%` }}
                    className={classes.bandTick}
                >
                    {fmtUsdK(eSignMin)}
                </span>
                <span
                    style={{ left: `${strategicStartPct}%` }}
                    className={classes.bandTick}
                >
                    {fmtUsdK(strategicAbove)}
                </span>
            </div>

            <div className={classes.bandLegend}>
                <span className={classes.bandLegendItem}>
                    <span className={`${classes.bandSwatch} ${classes.zoneAuto}`} />
                    Autonomous · &lt; {fmtUsdK(eSignMin)}
                </span>
                <span className={classes.bandLegendItem}>
                    <span className={`${classes.bandSwatch} ${classes.zoneEsign}`} />
                    E-sign · {fmtUsdK(eSignMin)}–{fmtUsdK(eSignMax)}
                </span>
                <span className={classes.bandLegendItem}>
                    <span className={`${classes.bandSwatch} ${classes.zoneStrategic}`} />
                    Strategic · &gt; {fmtUsdK(strategicAbove)}
                </span>
            </div>

        </Box>
    );
};

// ---------------------------------------------------------------------------
// 2. Levers (with policy walk-away floor footnote)
// ---------------------------------------------------------------------------

const LeversBox: React.FC<{ levers: Lever[] }> = ({ levers }) => {
    const inCount = levers.filter((l) => !isLeverOutOfEnvelope(l)).length;
    const tone: Tone =
        levers.length === 0
            ? "slate"
            : inCount === levers.length
              ? "green"
              : inCount === 0
                ? "red"
                : "amber";
    return (
        <Box
            title="Levers"
            headerRight={
                <StatusTag tone={tone}>
                    {inCount}/{levers.length}
                </StatusTag>
            }
        >
            {levers.map((l) => (
                <LeverBar key={l.name} lever={l} />
            ))}
        </Box>
    );
};

const LeverBar: React.FC<{ lever: Lever }> = ({ lever }) => {
    // Floor is the walk-away limit; the walk-away (red) side is the side of
    // the floor AWAY from the target. If target >= floor, walk-away is below
    // the floor (red on the left); otherwise it's above (red on the right).
    const walkAwayBelow = lever.targetPct >= lever.floorPct;
    const redLeft = walkAwayBelow ? 0 : lever.floorPct;
    const redWidth = walkAwayBelow ? lever.floorPct : 100 - lever.floorPct;
    const greenLeft = walkAwayBelow ? lever.floorPct : 0;
    const greenWidth = walkAwayBelow ? 100 - lever.floorPct : lever.floorPct;

    return (
        <div className={classes.leverItem}>
            <div className={classes.leverName}>{lever.name}</div>
            <div className={classes.leverChart}>
                <div
                    className={classes.leverValueLabel}
                    style={{ left: `${lever.currentPct}%` }}
                >
                    {lever.currentLabel}
                </div>
                <div
                    className={classes.leverTargetMarker}
                    style={{ left: `${lever.targetPct}%` }}
                    aria-label="Target"
                />
                <div className={classes.leverTrack}>
                    <div
                        className={classes.leverZoneGreen}
                        style={{ left: `${greenLeft}%`, width: `${greenWidth}%` }}
                    />
                    <div
                        className={classes.leverZoneRed}
                        style={{ left: `${redLeft}%`, width: `${redWidth}%` }}
                    />
                    <div
                        className={classes.dealMarker}
                        style={{ left: `${lever.currentPct}%` }}
                        aria-label="Current position"
                    />
                </div>
            </div>

            <div className={classes.leverTicks}>
                <span
                    style={{ left: `${lever.floorPct}%` }}
                    className={`${classes.leverTick} ${classes.leverTickCenter}`}
                >
                    {lever.floorLabel}
                </span>
                <span
                    style={{ left: `${lever.targetPct}%` }}
                    className={`${classes.leverTick} ${classes.leverTickCenter} ${classes.leverTickTarget}`}
                >
                    {/^target/i.test(lever.targetLabel)
                        ? lever.targetLabel
                        : `Target: ${lever.targetLabel}`}
                </span>
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// 3. Benchmark (with sources folded into a disclosure)
// ---------------------------------------------------------------------------

const BenchmarkBox: React.FC<{ benchmark: Benchmark }> = ({ benchmark }) => {
    const [sourcesOpen, setSourcesOpen] = React.useState(false);
    return (
        <Box title={`Benchmark · ${benchmark.metric}`} tourId="dealBench">
            <div className={classes.benchChart}>
                <div
                    className={classes.benchValueLabel}
                    style={{ left: `${benchmark.supplierPositionPct}%` }}
                >
                    {benchmark.supplierVsMarket}
                </div>
                <div className={classes.benchTrack}>
                    {benchmark.ticks.map((t, i) => (
                        <div
                            key={t.label}
                            className={`${classes.benchTick} ${
                                i === 1 ? classes.benchTickMedian : ""
                            }`}
                            style={{ left: `${t.positionPct}%` }}
                        />
                    ))}
                </div>
            </div>

            <div className={classes.benchTicks}>
                {benchmark.ticks.map((t) => (
                    <span
                        key={t.label}
                        className={classes.benchTickLabel}
                        style={{ left: `${t.positionPct}%` }}
                    >
                        {t.label}: {t.value}
                    </span>
                ))}
            </div>

            <div className={dealDrawerClasses.benchPills}>
                <StatusTag tone="slate">{benchmark.refreshedLabel}</StatusTag>
                <StatusTag tone={confidenceTone(benchmark.confidence)}>
                    Confidence: {benchmark.confidence}
                </StatusTag>
            </div>

            <p className={classes.rationale}>{benchmark.rationale}</p>

            <button
                type="button"
                className={classes.disclosure}
                onClick={() => setSourcesOpen((o) => !o)}
                aria-expanded={sourcesOpen}
            >
                {sourcesOpen ? "Hide" : "Show"} {benchmark.sources.length} source
                {benchmark.sources.length === 1 ? "" : "s"}
            </button>
            {sourcesOpen && (
                <ul className={classes.sources}>
                    {benchmark.sources.map((s, i) => (
                        <li key={i} className={classes.sourceItem}>
                            <span className={classes.sourceText}>{s}</span>
                        </li>
                    ))}
                </ul>
            )}
        </Box>
    );
};

function confidenceTone(c: Confidence): Tone {
    if (c === "high") return "green";
    if (c === "medium") return "amber";
    return "red";
}

// ---------------------------------------------------------------------------
// 4. Compliance — clauses, confidence, concession ladder
// ---------------------------------------------------------------------------

const ClauseIcon: React.FC<{ status: ClauseStatus }> = ({ status }) => {
    if (status === "confirmed")
        return <CheckCircleFilled className={classes.clauseOk} />;
    if (status === "exception")
        return <ExclamationCircleFilled className={classes.clauseWarn} />;
    return <MinusCircleOutlined className={classes.clausePending} />;
};

const ComplianceBox: React.FC<{ deal: Deal; policy: GuardrailPolicy }> = ({
    deal,
    policy,
}) => {
    const clauseStatuses = clauseStatusesFor(deal, policy.requiredClauses);
    const total = policy.requiredClauses.length;
    const confirmed = clauseStatuses.filter((s) => s === "confirmed").length;
    const hasException = clauseStatuses.some((s) => s === "exception");
    const tone: Tone =
        total === 0
            ? "slate"
            : hasException
              ? "red"
              : confirmed === total
                ? "green"
                : confirmed === 0
                  ? "red"
                  : "amber";

    return (
        <Box
            title="Compliance"
            headerRight={
                <StatusTag tone={tone}>
                    {confirmed}/{total}
                </StatusTag>
            }
        >
            <ul className={classes.clauseList}>
                {policy.requiredClauses.map((c, i) => (
                    <li key={c} className={classes.clauseItem}>
                        <ClauseIcon status={clauseStatuses[i] ?? "pending"} />
                        <span className={classes.clauseText}>{c}</span>
                    </li>
                ))}
            </ul>
        </Box>
    );
};

// ---------------------------------------------------------------------------
// Closed-deal boxes (kept for ClosedWon)
// ---------------------------------------------------------------------------

const FinalTermsBox: React.FC<{ outcome: Outcome }> = ({ outcome }) => (
    <Box title="Final terms vs incumbent">
        <table className={classes.termsTable}>
            <thead>
                <tr>
                    <th>Term</th>
                    <th>Was</th>
                    <th>Now</th>
                    <th>Delta</th>
                </tr>
            </thead>
            <tbody>
                {outcome.termChanges.map((t) => (
                    <tr key={t.lever}>
                        <td className={classes.termLever}>{t.lever}</td>
                        <td className={classes.termMuted}>{t.before}</td>
                        <td className={classes.termStrong}>{t.after}</td>
                        <td className={classes.termImpact}>{t.impact}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    </Box>
);

const RecordsBox: React.FC<{ outcome: Outcome }> = ({ outcome }) => (
    <Box title={outcome.binding.title}>
        <p className={classes.bindingDesc}>{outcome.binding.description}</p>
        {outcome.binding.files.map((f) => (
            <div key={f.title} className={dealDrawerClasses.fileBox}>
                <span className={dealDrawerClasses.pdf}>PDF</span>
                <div>
                    <div className={dealDrawerClasses.ft}>{f.title}</div>
                    <div className={dealDrawerClasses.fd}>{f.detail}</div>
                </div>
            </div>
        ))}
    </Box>
);
